/**
 * API Configuration
 * 
 * กำหนดค่า Backend ที่ใช้งาน
 * สามารถเปลี่ยนได้ผ่าน Environment Variable หรือ Runtime
 * 
 * ⚠️ Config ทั้งหมดอยู่ที่ @/config/app.config.ts
 */

import {
  PARSE_SERVER_URL,
  PARSE_APP_ID,
  PARSE_MASTER_KEY,
  PARSE_JS_KEY,
  PARSE_V2_SERVER_URL,
  PARSE_V2_APP_ID,
  PARSE_V2_MASTER_KEY,
  GO_SERVER_URL,
} from './app.config';

// ============================================
// Types
// ============================================

export type BackendType = 'parse-v1' | 'parse-v2' | 'go' | 'mock';

export interface BackendConfig {
  type: BackendType;
  name: string;
  description: string;
  serverURL: string;
  appId?: string;
  masterKey?: string;
  jsKey?: string;
}

// ============================================
// Backend Configurations
// ============================================

export const BACKEND_CONFIGS: Record<BackendType, BackendConfig> = {
  'parse-v1': {
    type: 'parse-v1',
    name: 'Parse Server V1',
    description: 'ระบบหลังบ้านเดิม (Legacy - สามารถสลับกลับมาใช้ได้)',
    serverURL: PARSE_SERVER_URL,
    appId: PARSE_APP_ID,
    masterKey: PARSE_MASTER_KEY,
    jsKey: PARSE_JS_KEY,
  },
  'parse-v2': {
    type: 'parse-v2',
    name: 'Parse Server V2',
    description: 'ระบบหลังบ้านใหม่ (Default)',
    serverURL: PARSE_V2_SERVER_URL,
    appId: PARSE_V2_APP_ID,
    masterKey: PARSE_V2_MASTER_KEY,
    jsKey: '',
  },
  'go': {
    type: 'go',
    name: 'Go + PostgreSQL',
    description: 'ระบบหลังบ้านใหม่ (Future)',
    serverURL: GO_SERVER_URL,
  },
  'mock': {
    type: 'mock',
    name: 'Mock Data',
    description: 'ข้อมูลจำลองสำหรับทดสอบ',
    serverURL: '',
  },
};

// ============================================
// Current Backend State
// ============================================

let currentBackend: BackendType = (import.meta.env.VITE_API_BACKEND as BackendType) || 'parse-v2';

/**
 * Get current backend type
 */
export function getBackendType(): BackendType {
  return currentBackend;
}

/**
 * Set backend type (runtime switching)
 */
export function setBackendType(type: BackendType): void {
  if (!BACKEND_CONFIGS[type]) {
    throw new Error(`Unknown backend type: ${type}`);
  }
  currentBackend = type;
  
  // Clear service caches
  clearServiceCaches();
  
  console.log(`🔄 Backend switched to: ${type}`);
}

/**
 * Get current backend config
 */
export function getBackendConfig(): BackendConfig {
  return BACKEND_CONFIGS[currentBackend];
}

/**
 * Get specific backend config
 */
export function getBackendConfigByType(type: BackendType): BackendConfig {
  return BACKEND_CONFIGS[type];
}

// ============================================
// Service Cache Management
// ============================================

type ServiceClearCallback = () => void;
const serviceClearCallbacks: ServiceClearCallback[] = [];

/**
 * Register a callback to clear service cache when backend changes
 */
export function registerServiceClearCallback(callback: ServiceClearCallback): void {
  serviceClearCallbacks.push(callback);
}

/**
 * Clear all service caches
 */
function clearServiceCaches(): void {
  serviceClearCallbacks.forEach(cb => cb());
}

// ============================================
// Helper Functions
// ============================================

/**
 * Check if current backend is production (V1)
 */
export function isProduction(): boolean {
  return currentBackend === 'parse-v1';
}

/**
 * Check if current backend is development (V2 or mock)
 */
export function isDevelopment(): boolean {
  return currentBackend === 'parse-v2' || currentBackend === 'mock';
}

/**
 * Get all available backends
 */
export function getAvailableBackends(): BackendConfig[] {
  return Object.values(BACKEND_CONFIGS);
}
